import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ChevronLeft, ShieldOff, Smartphone, RefreshCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Phase POLISH-1 #2 — Active Sessions admin view.
 *
 * Super-admin-only. Lists currently-valid (non-revoked, non-expired)
 * tokens for the user identified in the route. Each row has a Revoke
 * button (with confirmation dialog); the current request's own session
 * gets a "This device" chip and a "Sign out (this device)" action that
 * calls /auth/logout + bounces to /login.
 */

function fmt(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return s; }
}

function truncate(s, n = 56) {
    if (!s) return "—";
    return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

export default function AdminUserSessionsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: me, logout } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [revoking, setRevoking] = useState(null); // jti pending confirm
    const [revokeBusy, setRevokeBusy] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get(`/admin/users/${id}/sessions`);
            setRows(data || []);
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const doRevoke = async (jti) => {
        setRevokeBusy(true);
        try {
            await api.post(`/admin/users/${id}/sessions/${jti}/revoke`);
            toast.success("Session revoked");
            setRevoking(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setRevokeBusy(false); }
    };

    const doRevokeAllOthers = async () => {
        const targets = rows.filter((r) => !r.is_current).map((r) => r.jti);
        if (!targets.length) return;
        if (!window.confirm(`Revoke ${targets.length} other session(s)?`)) return;
        setBulkBusy(true);
        try {
            for (const jti of targets) {
                try { await api.post(`/admin/users/${id}/sessions/${jti}/revoke`); }
                catch { /* keep going — surfaced as count mismatch via reload */ }
            }
            toast.success("Other sessions revoked");
            await load();
        } finally { setBulkBusy(false); }
    };

    const doSignOutThisDevice = async () => {
        if (!window.confirm("Sign out this device?")) return;
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <DashboardShell>
            <div className="fade-up">
                <Link to="/admin/admins" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3">
                    <ChevronLeft size={14} /> Admins
                </Link>
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">
                    Sessions
                </p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                    Active sessions
                </h1>
                <p className="mt-2 text-sm text-slate-600 max-w-2xl">
                    Tokens currently valid for this user. Revoking a session signs that device out on its next request.
                </p>
            </div>

            <Card className="mt-6">
                <CardContent className="p-5 sm:p-6">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                            {loading ? "Loading…" : `${rows.length} active session${rows.length === 1 ? "" : "s"}`}
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline" size="sm"
                                onClick={load}
                                className="gap-1.5"
                                data-testid="sessions-refresh"
                            >
                                <RefreshCcw size={14} /> Refresh
                            </Button>
                            {rows.some((r) => !r.is_current) && (
                                <Button
                                    variant="outline" size="sm"
                                    disabled={bulkBusy}
                                    onClick={doRevokeAllOthers}
                                    className="gap-1.5"
                                    data-testid="sessions-revoke-all-others"
                                >
                                    <ShieldOff size={14} /> Revoke all other sessions
                                </Button>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div
                            className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
                            data-testid="sessions-error"
                        >{error}</div>
                    )}

                    {!loading && !error && rows.length === 0 && (
                        <p className="text-sm text-slate-500" data-testid="sessions-empty">
                            No active sessions on record.
                        </p>
                    )}

                    {!loading && rows.length > 0 && (
                        <div className="overflow-x-auto" data-testid="sessions-table">
                            <table className="min-w-full text-sm">
                                <thead className="text-xs uppercase tracking-wider text-slate-500">
                                    <tr className="border-b border-slate-200">
                                        <th className="text-left py-2 pr-3 font-medium">Issued at</th>
                                        <th className="text-left py-2 pr-3 font-medium">Last seen</th>
                                        <th className="text-left py-2 pr-3 font-medium">User agent</th>
                                        <th className="text-left py-2 pr-3 font-medium">IP</th>
                                        <th className="text-left py-2 pr-3 font-medium">Expires</th>
                                        <th className="text-right py-2 font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {rows.map((r) => (
                                        <tr
                                            key={r.jti}
                                            data-testid={`sessions-row-${r.jti}`}
                                            className={r.is_current ? "bg-blue-50/40" : ""}
                                        >
                                            <td className="py-2.5 pr-3 text-slate-700 align-top">{fmt(r.issued_at)}</td>
                                            <td className="py-2.5 pr-3 text-slate-700 align-top">{fmt(r.last_seen_at)}</td>
                                            <td className="py-2.5 pr-3 text-slate-700 align-top" title={r.user_agent || ""}>
                                                {truncate(r.user_agent)}
                                            </td>
                                            <td className="py-2.5 pr-3 text-slate-700 align-top">{r.ip || "—"}</td>
                                            <td className="py-2.5 pr-3 text-slate-600 align-top text-xs">{fmt(r.expires_at)}</td>
                                            <td className="py-2.5 align-top text-right whitespace-nowrap">
                                                {r.is_current ? (
                                                    <div className="flex items-center justify-end gap-2">
                                                        <span
                                                            className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-900 px-2 py-0.5 text-[11px] font-medium"
                                                            data-testid="sessions-this-device-chip"
                                                        >
                                                            <Smartphone size={12} /> This device
                                                        </span>
                                                        <Button
                                                            size="sm" variant="outline"
                                                            onClick={doSignOutThisDevice}
                                                            data-testid="sessions-signout-this-device"
                                                        >Sign out (this device)</Button>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        size="sm" variant="outline"
                                                        onClick={() => setRevoking(r.jti)}
                                                        data-testid={`sessions-revoke-${r.jti}`}
                                                    >Revoke</Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={!!revoking} onOpenChange={(o) => !o && !revokeBusy && setRevoking(null)}>
                <DialogContent
                    className="max-w-md"
                    data-testid="sessions-revoke-confirm-dialog"
                    onPointerDownOutside={(e) => revokeBusy && e.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>Revoke this session?</DialogTitle>
                        <DialogDescription>
                            The user will be signed out on their next request.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setRevoking(null)}
                            disabled={revokeBusy}
                            data-testid="sessions-revoke-cancel"
                        >Cancel</Button>
                        <Button
                            onClick={() => doRevoke(revoking)}
                            disabled={revokeBusy}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="sessions-revoke-confirm"
                        >{revokeBusy ? "Revoking…" : "Revoke session"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Suppress unused warning for `me` while still giving us a hook
                for future "show me which user this is" header copy. */}
            <span className="hidden" data-testid="sessions-current-user">{me?.id}</span>
        </DashboardShell>
    );
}
